import { CountryMarketConfig } from '../../common/CountryMarketConfig';
import {
  getApplicationKeysByParentKey,
  getMaxApplicationLevel,
} from '../../features/Briefing/StepApplication';
import { TaxonomyKeyRepresentation } from '../../features/Briefing/StepFormulationAttributes/types';
import { ProcessingCapabilitiesFields } from '../../features/Briefing/StepProcessingCapabilities/fieldDependencies';
import {
  ProductApplications,
  Taxonomies,
  Taxonomy,
  TaxonomyWithKey,
  TaxonomyWithKeyMultiselect,
} from '../../types/briefing';
import { BriefingState } from '../../types/misc';

// This data is mostly duplicated in fieldDependencies.ts:processingCapabilitiesFields, but
// we want to explicitly differentiate between fields to display and fields that are mandatory.
export const processingCapabilitiesPotentiallyRequiredFields: ProcessingCapabilitiesFields[] =
  [
    'bakeStability',
    'bakingTime',
    'bakingTemperature',
    'mixingStepDetails',
    'processingCapabilitiesPackagingDetails',
    'productPh',
    'brix',
    'fillingTime',
    'fillingTemperature',
    'foodSafetyDetails',
    'processingHotCold',
    'homogenisation',
  ];

export const isTaxonomyWithKeyMultiselect = (
  application: TaxonomyWithKey | TaxonomyWithKeyMultiselect,
): application is TaxonomyWithKeyMultiselect =>
  (application as TaxonomyWithKeyMultiselect)?.[0]?.key !== undefined;

export const isKeyInProductApplications = (
  key: TaxonomyKeyRepresentation,
  productApplications: ProductApplications,
): boolean =>
  productApplications
    .map((pa) => (isTaxonomyWithKeyMultiselect(pa) ? Object.values(pa) : pa))
    .flat()
    .some((pa) => pa?.key.match(key));

export const isKeyInProductApplicationsAtIndex = (
  key: TaxonomyKeyRepresentation,
  productApplications: ProductApplications,
  index: number,
): boolean => isKeyInProductApplications(key, [productApplications[index]]);

export const findIndexOfKeyInProductApplications = (
  key: string,
  productApplications: ProductApplications,
): number =>
  productApplications
    .map((pa) => (isTaxonomyWithKeyMultiselect(pa) ? Object.values(pa) : pa))
    .flat()
    .findIndex((pa) => pa?.key === key);

export const getProductApplicationKeysForIndex = (
  productApplications: ProductApplications,
  index: number,
): string[] | null => {
  const productApplication = productApplications[index];
  if (!productApplication) return null;

  const taxonomies = isTaxonomyWithKeyMultiselect(productApplication)
    ? Object.values(productApplication)
    : [productApplication];

  return taxonomies.map((pa) => pa.key);
};

export const getTaxonomyDisplayName = (
  tax: TaxonomyWithKey | TaxonomyWithKeyMultiselect,
  otherValue: string | null,
  otherLabel: string,
): string => {
  const taxonomies = isTaxonomyWithKeyMultiselect(tax)
    ? Object.values(tax)
    : [tax];

  return taxonomies
    .map(({ data }) =>
      data === otherLabel ? `${otherLabel}: ${otherValue}` : data ?? '',
    )
    .join(', ');
};

export const getMandatoryApplications: (
  productApplications: ProductApplications,
  availableTaxonomies: Taxonomies,
  countryMarketConfig: CountryMarketConfig | null | undefined,
) => keyof BriefingState | boolean = (
  productApplications,
  availableTaxonomies,
  countryMarketConfig,
) => {
  const parentKey = productApplications[0]?.key.toString() || '';
  const nextOptions = getApplicationKeysByParentKey(
    parentKey,
    availableTaxonomies,
  );

  // If there are no further options (aside from Other), don't show any more and allow
  // the user to continue. Part 2 of this functionality is in StepApplication:useEffect - search
  // for text similar to this.
  const maxApplicationLevel =
    nextOptions.length === 0
      ? 1
      : getMaxApplicationLevel(
          availableTaxonomies,
          countryMarketConfig?.taxonomyMarketAbbreviaton,
        );

  if (isKeyInProductApplications('other', productApplications)) {
    return 'productApplicationsOther';
  }

  if (productApplications.length < maxApplicationLevel) {
    return false; // This is to force Next to be disabled if we haven't selected a full amount of productApplications
  }
  return 'productApplications';
};

export const getTaxonomyByPreferredLocale = (
  applicationKey: string,
  availableTaxonomies: Taxonomies,
  userLocale: string,
): Taxonomy =>
  availableTaxonomies[applicationKey].find(
    ({ locale }) => locale === userLocale,
  ) ||
  availableTaxonomies[applicationKey].find(
    ({ locale }) => locale === 'en_us',
  ) ||
  availableTaxonomies[applicationKey][0];
